import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  MarkerType,
  Position,
  Handle,
  NodeProps,
  getBezierPath,
  EdgeProps
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import Editor from '@monaco-editor/react';
import JsonView from '@uiw/react-json-view';

// Types
export interface DebugNodeData {
  label: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  duration?: number;
  input?: any;
  output?: any;
  error?: Error | string;
  metadata?: {
    tokens?: number;
    memory?: number;
    timestamp: number;
    stepIndex?: number;
  };
  analytics?: {
    performance?: any;
    data?: any;
    quality?: any;
  };
}

interface DebugPanelEnhancedProps {
  isOpen: boolean;
  onClose: () => void;
  debugSteps: Array<{
    id: string;
    name: string;
    status: 'pending' | 'running' | 'completed' | 'error';
    duration?: number;
    error?: string;
    input?: any;
    output?: any;
    analytics?: any;
    aiObservability?: any;
  }>;
  optimizationStats?: any;
  currentState?: {
    phase: string;
    anchorsCount: number;
    selectedAnchorsCount: number;
    zenLensesCount: number;
    activeLensId?: string;
    isGenerating: boolean;
  };
  aiObservabilityData?: Map<string, any>;
}

// Custom Node Component
const DebugNode: React.FC<NodeProps<DebugNodeData>> = ({ data, selected }) => {
  const statusColors = {
    pending: '#718096',
    running: '#4299e1',
    completed: '#48bb78',
    error: '#ef4444'
  };

  const statusIcons = {
    pending: '○',
    running: '⏳',
    completed: '✓',
    error: '✕'
  };

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className={`debug-node ${data.status} ${selected ? 'selected' : ''}`}
      style={{
        background: 'var(--zen-card)',
        border: `2px solid ${statusColors[data.status]}`,
        borderRadius: '8px',
        padding: '12px',
        minWidth: '200px',
        boxShadow: selected ? `0 0 0 2px ${statusColors[data.status]}40` : '0 2px 4px rgba(0,0,0,0.2)'
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: statusColors[data.status] }} />
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '16px' }}>{statusIcons[data.status]}</span>
        <span style={{ fontWeight: 600, color: 'var(--zen-text)' }}>{data.label}</span>
      </div>
      
      {data.duration && (
        <div style={{ fontSize: '11px', color: 'var(--zen-text-muted)' }}>
          Duration: {data.duration}ms
        </div>
      )}
      
      {data.metadata?.tokens && (
        <div style={{ fontSize: '11px', color: 'var(--zen-text-muted)' }}>
          Tokens: {data.metadata.tokens}
        </div>
      )}
      
      {data.error && (
        <div style={{ 
          fontSize: '11px', 
          color: '#ef4444',
          marginTop: '4px',
          padding: '4px',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '4px'
        }}>
          {typeof data.error === 'string' ? data.error : data.error.message}
        </div>
      )}
      
      <Handle type="source" position={Position.Bottom} style={{ background: statusColors[data.status] }} />
    </motion.div>
  );
};

// Custom Edge Component
const AnimatedEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data
}) => {
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
      />
      {data?.animated && (
        <circle r="4" fill="#4299e1">
          <animateMotion dur="2s" repeatCount="indefinite" path={edgePath} />
        </circle>
      )}
    </>
  );
};

const nodeTypes = {
  debugNode: DebugNode,
};

const edgeTypes = {
  animated: AnimatedEdge,
};

// Helper function to determine if a step is the final step
const isFinalStep = (stepId: string | null, debugSteps: any[]): boolean => {
  if (!stepId || debugSteps.length === 0) return false;
  
  // The final step is the last one in the pipeline
  const lastStep = debugSteps[debugSteps.length - 1];
  return stepId === lastStep.id;
};

// Fullscreen wrapper for data viewer
const DataViewerFullscreenWrapper: React.FC<{
  isFullscreen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title: string;
  dataViewMode: 'json-tree' | 'monaco' | 'table' | 'grid';
  setDataViewMode: (mode: 'json-tree' | 'monaco' | 'table' | 'grid') => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}> = ({ isFullscreen, onClose, children, title, dataViewMode, setDataViewMode, searchQuery, setSearchQuery }) => {
  // Handle ESC key to exit fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullscreen, onClose]);
  
  if (!isFullscreen) return <>{children}</>;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'var(--zen-pane)',
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{
        padding: '16px 24px',
        background: 'var(--zen-card)',
        borderBottom: '1px solid var(--zen-border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--zen-text)' }}>
            {title} - Fullscreen View
          </h3>
          
          {/* Data View Controls */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={dataViewMode}
              onChange={(e) => setDataViewMode(e.target.value as any)}
              style={{
                padding: '6px 10px',
                background: 'var(--zen-card)',
                color: 'var(--zen-text)',
                border: '1px solid var(--zen-border)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              <option value="json-tree">🌳 Tree View</option>
              <option value="monaco">📝 Code View</option>
              <option value="table">📊 Table View</option>
              <option value="grid">📋 Grid View</option>
            </select>
            
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 10px',
                background: 'var(--zen-card)',
                color: 'var(--zen-text)',
                border: '1px solid var(--zen-border)',
                borderRadius: '4px',
                fontSize: '12px',
                width: '150px'
              }}
            />
          </div>
        </div>
        
        <button
          onClick={onClose}
          title="Exit Fullscreen (Esc)"
          style={{
            padding: '8px 12px',
            background: 'transparent',
            border: '1px solid var(--zen-border)',
            borderRadius: '4px',
            color: 'var(--zen-text)',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <span>✕</span>
          <span style={{ fontSize: '12px' }}>Exit</span>
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {children}
      </div>
    </motion.div>
  );
};

// Helper functions for Grid View
const findArraysInData = (obj: any, path = '$'): Array<{path: string, length: number, sampleKeys: string[]}> => {
  const arrays: Array<{path: string, length: number, sampleKeys: string[]}> = [];
  
  const traverse = (current: any, currentPath: string) => {
    if (Array.isArray(current) && current.length > 0) {
      // Check if it's an array of objects
      if (typeof current[0] === 'object' && current[0] !== null) {
        const sampleKeys = Object.keys(current[0]);
        arrays.push({
          path: currentPath,
          length: current.length,
          sampleKeys: sampleKeys.slice(0, 5) // First 5 keys as sample
        });
      }
    } else if (current && typeof current === 'object') {
      Object.keys(current).forEach(key => {
        traverse(current[key], `${currentPath}.${key}`);
      });
    }
  };
  
  traverse(obj, path);
  return arrays;
};

const flattenObjectForGrid = (obj: any, parentPath = ''): Record<string, any> => {
  const flattened: Record<string, any> = {};
  
  Object.keys(obj || {}).forEach(key => {
    const value = obj[key];
    const path = parentPath ? `${parentPath}.${key}` : key;
    
    if (value === null || value === undefined) {
      flattened[path] = value;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Flatten nested objects up to 2 levels deep
      if (parentPath.split('.').length < 2) {
        Object.assign(flattened, flattenObjectForGrid(value, path));
      } else {
        flattened[path] = JSON.stringify(value);
      }
    } else if (Array.isArray(value)) {
      flattened[path] = `Array(${value.length})`;
    } else {
      flattened[path] = value;
    }
  });
  
  return flattened;
};

// Enhanced Data Viewer Component
const EnhancedDataViewer: React.FC<{
  data: any;
  mode: 'json-tree' | 'monaco' | 'table' | 'grid';
  searchQuery: string;
  title: string;
  onFullscreenToggle?: () => void;
  isFullscreen?: boolean;
}> = ({ data, mode, searchQuery, title, onFullscreenToggle, isFullscreen = false }) => {
  // Grid view state - MUST be declared at top level, not conditionally
  const [selectedArrayPath, setSelectedArrayPath] = useState<string>('');
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    
    // Simple search filter for JSON data
    const filterObject = (obj: any): any => {
      if (typeof obj === 'string') {
        return obj.toLowerCase().includes(searchQuery.toLowerCase()) ? obj : null;
      }
      if (Array.isArray(obj)) {
        return obj.map(filterObject).filter(item => item !== null);
      }
      if (obj && typeof obj === 'object') {
        const filtered: any = {};
        Object.keys(obj).forEach(key => {
          if (key.toLowerCase().includes(searchQuery.toLowerCase())) {
            filtered[key] = obj[key];
          } else {
            const filteredValue = filterObject(obj[key]);
            if (filteredValue !== null && Object.keys(filteredValue || {}).length > 0) {
              filtered[key] = filteredValue;
            }
          }
        });
        return Object.keys(filtered).length > 0 ? filtered : null;
      }
      return obj;
    };
    
    return filterObject(data) || { message: "No matches found" };
  }, [data, searchQuery]);
  
  // Grid view computed values - always calculated but only used in grid mode
  const detectedArrays = useMemo(() => findArraysInData(filteredData), [filteredData]);
  
  // Auto-select first array or top-level if it's an array
  useEffect(() => {
    if (mode === 'grid') {
      if (Array.isArray(filteredData)) {
        setSelectedArrayPath('$');
      } else if (detectedArrays.length > 0 && !selectedArrayPath) {
        setSelectedArrayPath(detectedArrays[0].path);
      }
    }
  }, [filteredData, detectedArrays, mode, selectedArrayPath]);
  
  // Get the array data based on selected path
  const getArrayFromPath = useCallback((obj: any, path: string): any[] => {
    if (path === '$') return Array.isArray(obj) ? obj : [];
    
    const parts = path.substring(2).split('.');
    let current = obj;
    for (const part of parts) {
      if (current && current[part] !== undefined) {
        current = current[part];
      } else {
        return [];
      }
    }
    return Array.isArray(current) ? current : [];
  }, []);
  
  const gridData = useMemo(() => {
    if (mode !== 'grid') return { rows: [], columns: [] };
    
    const arrayData = getArrayFromPath(filteredData, selectedArrayPath);
    if (arrayData.length === 0) return { rows: [], columns: [] };
    
    // Extract columns from first 100 rows for performance
    const columnsSet = new Set<string>();
    const flattenedRows: any[] = [];
    
    arrayData.forEach((row, index) => {
      if (typeof row === 'object' && row !== null) {
        const flattened = flattenObjectForGrid(row);
        Object.keys(flattened).forEach(key => columnsSet.add(key));
        flattenedRows.push({
          __index: index,
          __jsonPointer: `${selectedArrayPath}[${index}]`,
          ...flattened
        });
      } else {
        flattenedRows.push({
          __index: index,
          __jsonPointer: `${selectedArrayPath}[${index}]`,
          value: row
        });
      }
    });
    
    const columns = Array.from(columnsSet).sort();
    return { rows: flattenedRows, columns };
  }, [filteredData, selectedArrayPath, mode, getArrayFromPath]);

  const generateTableView = (obj: any, parentKey = ''): JSX.Element[] => {
    const rows: JSX.Element[] = [];
    
    Object.entries(obj || {}).forEach(([key, value], index) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Nested object - create expandable row
        rows.push(
          <tr key={fullKey} style={{ background: 'var(--zen-card)' }}>
            <td style={{ 
              padding: '8px', 
              border: '1px solid var(--zen-border)', 
              fontWeight: 600,
              color: 'var(--zen-accent)'
            }}>
              📁 {key}
            </td>
            <td style={{ 
              padding: '8px', 
              border: '1px solid var(--zen-border)',
              color: 'var(--zen-text-muted)',
              fontSize: '12px'
            }}>
              {typeof value} ({Object.keys(value).length} properties)
            </td>
          </tr>
        );
        // Add nested rows
        rows.push(...generateTableView(value, fullKey));
      } else {
        // Simple value
        rows.push(
          <tr key={fullKey}>
            <td style={{ 
              padding: '8px', 
              border: '1px solid var(--zen-border)',
              fontWeight: 500,
              color: 'var(--zen-text)'
            }}>
              {parentKey ? `├─ ${key}` : key}
            </td>
            <td style={{ 
              padding: '8px', 
              border: '1px solid var(--zen-border)',
              color: Array.isArray(value) ? 'var(--zen-accent)' : 'var(--zen-text)',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}>
              {Array.isArray(value) 
                ? `Array(${value.length})` 
                : String(value).length > 100 
                  ? String(value).substring(0, 100) + '...'
                  : String(value)
              }
            </td>
          </tr>
        );
      }
    });
    
    return rows;
  };

  if (mode === 'json-tree') {
    return (
      <div style={{ 
        height: '100%', 
        overflow: 'auto', 
        padding: '12px',
        textAlign: 'left' // Fix center alignment
      }}>
        <JsonView 
          value={filteredData}
          collapsed={2}
          displayDataTypes={true}
          displayObjectSize={true}
          enableClipboard={true}
          style={{
            backgroundColor: 'transparent',
            fontSize: '13px',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            lineHeight: '1.5',
            textAlign: 'left',
            '--w-rjv-font-family': 'Monaco, Consolas, "Courier New", monospace',
            '--w-rjv-color': '#e2e8f0',
            '--w-rjv-key-string': '#e2e8f0',
            '--w-rjv-background-color': 'transparent',
            '--w-rjv-line-color': '#2d3748',
            '--w-rjv-arrow-color': '#a0aec0',
            '--w-rjv-edit-color': '#4299e1',
            '--w-rjv-info-color': '#718096',
            '--w-rjv-update-color': '#48bb78',
            '--w-rjv-copied-color': '#4299e1',
            '--w-rjv-copied-success-color': '#48bb78',
            '--w-rjv-curlybraces-color': '#a0aec0',
            '--w-rjv-colon-color': '#e2e8f0',
            '--w-rjv-brackets-color': '#a0aec0',
            '--w-rjv-quotes-color': '#a0aec0',
            '--w-rjv-quotes-string-color': '#48bb78',
            '--w-rjv-type-string-color': '#48bb78',
            '--w-rjv-type-int-color': '#ed8936',
            '--w-rjv-type-float-color': '#ed8936',
            '--w-rjv-type-bigint-color': '#ed8936',
            '--w-rjv-type-boolean-color': '#4299e1',
            '--w-rjv-type-date-color': '#b794f6',
            '--w-rjv-type-null-color': '#f56565',
            '--w-rjv-type-nan-color': '#f56565',
            '--w-rjv-type-undefined-color': '#a0aec0'
          } as React.CSSProperties}
        />
      </div>
    );
  }

  if (mode === 'table') {
    return (
      <div style={{ height: '100%', overflow: 'auto', padding: '12px' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '12px'
        }}>
          <thead>
            <tr style={{ background: 'var(--zen-card)' }}>
              <th style={{ 
                padding: '12px 8px', 
                border: '1px solid var(--zen-border)',
                textAlign: 'left',
                fontWeight: 600,
                color: 'var(--zen-text)'
              }}>
                Property
              </th>
              <th style={{ 
                padding: '12px 8px', 
                border: '1px solid var(--zen-border)',
                textAlign: 'left',
                fontWeight: 600,
                color: 'var(--zen-text)'
              }}>
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {generateTableView(filteredData)}
          </tbody>
        </table>
      </div>
    );
  }

  if (mode === 'grid') {
    // Grid view for arrays and tabular data
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Array Selector */}
        {detectedArrays.length > 0 && (
          <div style={{
            padding: '8px 12px',
            background: 'var(--zen-card)',
            borderBottom: '1px solid var(--zen-border)',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '11px', color: 'var(--zen-text-muted)' }}>
              Arrays detected:
            </span>
            {detectedArrays.map(arr => (
              <button
                key={arr.path}
                onClick={() => setSelectedArrayPath(arr.path)}
                style={{
                  padding: '4px 8px',
                  background: selectedArrayPath === arr.path ? 'var(--zen-accent)' : 'var(--zen-pane)',
                  color: selectedArrayPath === arr.path ? 'white' : 'var(--zen-text)',
                  border: '1px solid var(--zen-border)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}
              >
                <span>{arr.path}</span>
                <span style={{ opacity: 0.7 }}>({arr.length})</span>
              </button>
            ))}
          </div>
        )}
        
        {/* Grid Display */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          {gridData.rows.length > 0 ? (
            <div style={{ minWidth: '100%', overflow: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '11px',
                fontFamily: 'Monaco, Consolas, monospace'
              }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--zen-card)', zIndex: 10 }}>
                  <tr>
                    <th style={{
                      padding: '8px',
                      border: '1px solid var(--zen-border)',
                      textAlign: 'left',
                      fontWeight: 600,
                      color: 'var(--zen-text)',
                      background: 'var(--zen-card)',
                      minWidth: '50px'
                    }}>
                      #
                    </th>
                    {gridData.columns.map(col => (
                      <th key={col} style={{
                        padding: '8px',
                        border: '1px solid var(--zen-border)',
                        textAlign: 'left',
                        fontWeight: 600,
                        color: 'var(--zen-text)',
                        background: 'var(--zen-card)',
                        minWidth: '100px',
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gridData.rows.slice(0, 500).map((row, rowIndex) => (
                    <tr key={row.__jsonPointer} style={{
                      background: rowIndex % 2 === 0 ? 'var(--zen-pane)' : 'var(--zen-card)'
                    }}>
                      <td style={{
                        padding: '8px',
                        border: '1px solid var(--zen-border)',
                        color: 'var(--zen-text-muted)',
                        fontWeight: 500
                      }}>
                        {row.__index}
                      </td>
                      {gridData.columns.map(col => {
                        const value = row[col];
                        const cellKey = `${row.__jsonPointer}-${col}`;
                        const isExpanded = expandedCells.has(cellKey);
                        const isHovered = hoveredCell === cellKey;
                        const isNull = value === null;
                        const isUndefined = value === undefined;
                        const isString = typeof value === 'string';
                        const isNumber = typeof value === 'number';
                        const isBoolean = typeof value === 'boolean';
                        const valueStr = String(value);
                        const needsExpansion = valueStr.length > 50;
                        
                        return (
                          <td 
                            key={col} 
                            onMouseEnter={() => setHoveredCell(cellKey)}
                            onMouseLeave={() => setHoveredCell(null)}
                            style={{
                              padding: '8px',
                              border: '1px solid var(--zen-border)',
                              color: isNull || isUndefined ? 'var(--zen-text-muted)' : 
                                     isNumber ? '#4299e1' :
                                     isBoolean ? '#48bb78' :
                                     isString ? 'var(--zen-text)' : 'var(--zen-accent)',
                              fontStyle: isNull || isUndefined ? 'italic' : 'normal',
                              maxWidth: isExpanded ? 'none' : '300px',
                              minWidth: '100px',
                              overflow: 'hidden',
                              textOverflow: isExpanded ? 'clip' : 'ellipsis',
                              whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                              wordBreak: isExpanded ? 'break-word' : 'normal',
                              cursor: needsExpansion ? 'pointer' : 'default',
                              position: 'relative',
                              background: isHovered ? 'rgba(66, 153, 225, 0.1)' : 'transparent'
                            }}
                            onClick={() => {
                              if (needsExpansion) {
                                const newExpanded = new Set(expandedCells);
                                if (isExpanded) {
                                  newExpanded.delete(cellKey);
                                } else {
                                  newExpanded.add(cellKey);
                                }
                                setExpandedCells(newExpanded);
                              }
                            }}
                          >
                            {/* Cell Content */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>
                                {isNull ? 'null' : 
                                 isUndefined ? 'undefined' :
                                 isBoolean ? (value ? '✓' : '✗') :
                                 isExpanded ? valueStr : 
                                 needsExpansion ? valueStr.substring(0, 50) + '...' : valueStr}
                              </span>
                              
                              {/* Action buttons */}
                              {needsExpansion && (
                                <span style={{ 
                                  fontSize: '10px', 
                                  opacity: 0.5,
                                  marginLeft: '4px'
                                }}>
                                  {isExpanded ? '▼' : '▶'}
                                </span>
                              )}
                              
                              {/* Copy button on hover */}
                              {isHovered && !isNull && !isUndefined && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(valueStr);
                                    // Show feedback
                                    const btn = e.currentTarget;
                                    btn.textContent = '✓';
                                    setTimeout(() => { btn.textContent = '📋'; }, 1000);
                                  }}
                                  style={{
                                    marginLeft: 'auto',
                                    padding: '2px 4px',
                                    background: 'var(--zen-card)',
                                    border: '1px solid var(--zen-border)',
                                    borderRadius: '2px',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    lineHeight: 1
                                  }}
                                  title="Copy to clipboard"
                                >
                                  📋
                                </button>
                              )}
                            </div>
                            
                            {/* Tooltip for long text */}
                            {isHovered && needsExpansion && !isExpanded && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                zIndex: 1000,
                                background: 'var(--zen-card)',
                                border: '1px solid var(--zen-border)',
                                borderRadius: '4px',
                                padding: '8px',
                                maxWidth: '400px',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                                fontSize: '11px',
                                marginTop: '4px'
                              }}>
                                {valueStr}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {gridData.rows.length > 500 && (
                <div style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: 'var(--zen-text-muted)',
                  fontSize: '11px',
                  background: 'var(--zen-card)',
                  borderTop: '1px solid var(--zen-border)',
                  marginTop: '12px'
                }}>
                  Showing first 500 of {gridData.rows.length} rows (switch to Monaco view for full data)
                </div>
              )}
            </div>
          ) : (
            <div style={{
              padding: '24px',
              textAlign: 'center',
              color: 'var(--zen-text-muted)'
            }}>
              No array data found. Try selecting a different path or switch to Tree/Table view.
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Default Monaco editor
  return (
    <div style={{ height: '100%' }}>
      <Editor
        height="100%"
        defaultLanguage="json"
        value={JSON.stringify(filteredData, null, 2)}
        theme="vs-dark"
        options={{
          readOnly: true,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 12,
          wordWrap: 'on',
          folding: true,
          lineNumbers: 'on',
          renderWhitespace: 'selection'
        }}
      />
    </div>
  );
};

export const DebugPanelEnhanced: React.FC<DebugPanelEnhancedProps> = ({
  isOpen,
  onClose,
  debugSteps,
  optimizationStats,
  currentState,
  aiObservabilityData
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [showInputOutput, setShowInputOutput] = useState(true);
  const [showOutput, setShowOutput] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [viewMode, setViewMode] = useState<'graph' | 'list' | 'ai-observability'>('graph');
  const [dataViewMode, setDataViewMode] = useState<'json-tree' | 'monaco' | 'table' | 'grid'>('json-tree');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [inputFullscreen, setInputFullscreen] = useState(false);
  const [outputFullscreen, setOutputFullscreen] = useState(false);

  // Convert debug steps to nodes and edges
  const initialNodes = useMemo(() => {
    
    return debugSteps.map((step, index) => ({
      id: step.id,
      type: 'debugNode',
      position: { 
        x: 250 * (index % 3), 
        y: 150 * Math.floor(index / 3) 
      },
      data: {
        label: step.name,
        status: step.status,
        duration: step.duration,
        error: step.error,
        input: step.input,
        output: step.output, // This is the critical data!
        metadata: {
          timestamp: step.metadata?.timestamp || Date.now(),
          stepIndex: index
        },
        analytics: step.analytics
      } as DebugNodeData
    }));
  }, [debugSteps]);

  const initialEdges = useMemo(() => {
    const edges: Edge[] = [];
    for (let i = 0; i < debugSteps.length - 1; i++) {
      edges.push({
        id: `e${i}-${i + 1}`,
        source: debugSteps[i].id,
        target: debugSteps[i + 1].id,
        type: 'animated',
        animated: debugSteps[i].status === 'running',
        style: { 
          stroke: debugSteps[i].status === 'error' ? '#ef4444' : '#4299e1',
          strokeWidth: 2 
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: debugSteps[i].status === 'error' ? '#ef4444' : '#4299e1'
        },
        data: { animated: debugSteps[i].status === 'running' }
      });
    }
    return edges;
  }, [debugSteps]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes when debug steps change
  useEffect(() => {
    console.log('🔄 Nodes/Edges updating due to dependencies change:', {
      debugStepsCount: debugSteps.length,
      initialNodesCount: initialNodes.length,
      initialEdgesCount: initialEdges.length,
      debugStepsIds: debugSteps.map(s => s.id),
      initialNodesIds: initialNodes.map(n => n.id)
    });
    
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [debugSteps, initialNodes, initialEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node.id);
    // Auto-enable output for generate step
    if (node.id === 'generate') {
      setShowOutput(true);
    }
  }, []);

  const selectedNodeData = useMemo(() => {
    if (!selectedNode) return null;
    const node = nodes.find(n => n.id === selectedNode);
    return node?.data as DebugNodeData | undefined;
  }, [selectedNode, nodes]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 500 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 500 }}
        className={`debug-panel-enhanced ${isFullscreen ? 'fullscreen' : ''}`}
        style={{
          position: 'fixed',
          top: isFullscreen ? 0 : 60,
          right: 0,
          bottom: 0,
          width: isFullscreen ? '100vw' : '60vw',
          background: 'var(--zen-pane)',
          borderLeft: isFullscreen ? 'none' : '2px solid var(--zen-border)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          background: 'var(--zen-card)',
          borderBottom: '1px solid var(--zen-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--zen-text)', margin: 0 }}>
              🔍 AI Debug Observatory
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setViewMode('graph')}
                style={{
                  padding: '6px 12px',
                  background: viewMode === 'graph' ? 'var(--zen-accent)' : 'transparent',
                  color: viewMode === 'graph' ? 'white' : 'var(--zen-text)',
                  border: '1px solid var(--zen-border)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Graph View
              </button>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '6px 12px',
                  background: viewMode === 'list' ? 'var(--zen-accent)' : 'transparent',
                  color: viewMode === 'list' ? 'white' : 'var(--zen-text)',
                  border: '1px solid var(--zen-border)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                List View
              </button>
              <button
                onClick={() => setViewMode('ai-observability')}
                style={{
                  padding: '6px 12px',
                  background: viewMode === 'ai-observability' ? 'var(--zen-accent)' : 'transparent',
                  color: viewMode === 'ai-observability' ? 'white' : 'var(--zen-text)',
                  border: '1px solid var(--zen-border)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🤖 AI Observability
              </button>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Export Data Button */}
            <button
              onClick={() => {
                const exportData = {
                  timestamp: new Date().toISOString(),
                  debugSteps: debugSteps,
                  currentState: currentState,
                  optimizationStats: optimizationStats,
                  aiObservabilityData: aiObservabilityData ? Object.fromEntries(aiObservabilityData.entries()) : {},
                  astValidationDetails: debugSteps
                    .filter(step => step.id === 'resolve' || step.name.toLowerCase().includes('ast'))
                    .map(step => ({
                      stepId: step.id,
                      stepName: step.name,
                      input: step.input,
                      output: step.output,
                      duration: step.duration,
                      error: step.error,
                      astDebugInfo: step.output?.astDebugInfo || step.input?.astDebugInfo
                    })),
                  metadata: {
                    totalSteps: debugSteps.length,
                    completedSteps: debugSteps.filter(s => s.status === 'completed').length,
                    errorSteps: debugSteps.filter(s => s.status === 'error').length,
                    totalDuration: debugSteps.reduce((sum, s) => sum + (s.duration || 0), 0),
                    astValidationSteps: debugSteps.filter(step => step.id === 'resolve' || step.name.toLowerCase().includes('ast')).length
                  }
                };
                
                const dataStr = JSON.stringify(exportData, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(dataBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `debug-data-${Date.now()}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
              title="Export Debug Data (JSON)"
              style={{
                padding: '8px',
                background: 'transparent',
                border: '1px solid var(--zen-border)',
                borderRadius: '4px',
                color: 'var(--zen-text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              💾
            </button>
            
            {/* Export LLM Prompt Button */}
            <button
              onClick={() => {
                const generateLLMPrompt = () => {
                  const timestamp = new Date().toISOString();
                  const completedSteps = debugSteps.filter(s => s.status === 'completed');
                  const errorSteps = debugSteps.filter(s => s.status === 'error');
                  const astValidationSteps = debugSteps.filter(step => step.id === 'resolve' || step.name.toLowerCase().includes('ast'));
                  
                  return `# SyntaxLens Debug Analysis Request

## Context
I'm working on SyntaxLens, an educational tool that helps developers learn new programming languages by creating interactive "lenses" that explain code differences. I need help analyzing and improving this debug session, with particular focus on AST validation issues.

**Session Info:**
- Timestamp: ${timestamp}
- Pipeline: LLM-first semantic selection → AST validation → Lens generation
- Steps Completed: ${completedSteps.length}/${debugSteps.length}
- Errors: ${errorSteps.length}
- AST Validation Steps: ${astValidationSteps.length}
- Total Duration: ${debugSteps.reduce((sum, s) => sum + (s.duration || 0), 0)}ms

## Pipeline Overview

The system works in these steps:
1. **Parse**: Tree-sitter AST parsing of target code
2. **LLM Semantic Picks**: Claude analyzes code and selects pedagogically valuable elements
3. **Validate with AST**: Match LLM selections to precise AST positions (CRITICAL ISSUE AREA)
4. **Smart Selection**: Convert LLM picks to anchor selections
5. **Generate Lenses**: Create educational content (surface/concept/paradigm) for each selection

## Known Issues Requiring Analysis
- **AST Partial Match Problem**: AST validation is using partial token matches (e.g., just "fn") instead of full LLM selections (e.g., "fn create_user() -> Box<User>")
- **Highlighting Accuracy**: Text positions are being miscalculated, causing incorrect highlighting
- **Span Validation**: Need better validation between LLM selections and AST node spans

<file_map>
syntaxlens/
├── src/
│   ├── components/
│   │   ├── ZenPlaygroundOptimized.tsx *
│   │   └── DebugPanelEnhanced.tsx *
│   ├── services/
│   │   ├── codeFirstSelection.ts *
│   │   └── lensAnchorService.ts *
│   └── utils/
│       └── treeSitterParser.ts *
├── backend/
│   └── server.js *
└── debug_session.json *
</file_map>

<file_contents>
File: syntaxlens/debug_session.json
\`\`\`json
${JSON.stringify({
  sessionMetadata: {
    timestamp,
    pipeline: "LLM-first semantic selection",
    totalSteps: debugSteps.length,
    completedSteps: completedSteps.length,
    errorSteps: errorSteps.length,
    totalDuration: debugSteps.reduce((sum, s) => sum + (s.duration || 0), 0)
  },
  debugSteps: debugSteps.map(step => ({
    id: step.id,
    name: step.name,
    status: step.status,
    duration: step.duration,
    input: step.input,
    output: step.output,
    error: step.error,
    analytics: step.analytics,
    aiObservability: aiObservabilityData?.get(step.id),
    astDebugInfo: step.output?.astDebugInfo || step.input?.astDebugInfo
  })),
  aiObservabilityData: aiObservabilityData ? Object.fromEntries(aiObservabilityData.entries()) : {},
  currentState,
  optimizationStats
}, null, 2)}
\`\`\`

File: syntaxlens/pipeline_summary.md
\`\`\`markdown
# Pipeline Execution Summary

## Steps Executed:
${debugSteps.map((step, i) => `
### ${i + 1}. ${step.name} (${step.status})
- Duration: ${step.duration || 0}ms
- Input: ${step.input ? Object.keys(step.input).join(', ') : 'None'}
- Output: ${step.output ? Object.keys(step.output).join(', ') : 'None'}
${step.error ? `- Error: ${step.error}` : ''}
`).join('')}

## Performance Metrics:
- Fastest Step: ${Math.min(...debugSteps.filter(s => s.duration).map(s => s.duration!))}ms
- Slowest Step: ${Math.max(...debugSteps.filter(s => s.duration).map(s => s.duration!))}ms
- Success Rate: ${(completedSteps.length / debugSteps.length * 100).toFixed(1)}%

## AI Observability Metrics:
${aiObservabilityData ? Array.from(aiObservabilityData.entries()).map(([stepId, data]) => `
### ${stepId.startsWith('semantic') ? '🧠 LLM Semantic Picks' : '🌸 Generate Lenses'} API (${new Date(data.apiCall.timestamp).toLocaleTimeString()})
- Endpoint: ${data.apiCall.endpoint}
- Response Time: ${data.response.responseTimeMs}ms
- Status: ${data.response.statusCode}
- Model: ${data.metadata.model || 'N/A'}
- Input Tokens: ${data.prompt.tokenCount || 'N/A'}
- Output Tokens: ${data.response.tokenCount || 'N/A'}
- Estimated Cost: ${data.metadata.cost ? '$' + data.metadata.cost.toFixed(6) : 'N/A'}
- Prompt Length: ${data.prompt.content.length} chars
`).join('') : '- No AI observability data captured'}

## AST Validation Issues:
${astValidationSteps.length > 0 ? astValidationSteps.map(step => {
  const astDebug = step.output?.astDebugInfo || step.input?.astDebugInfo;
  return astDebug ? `
### ${step.name} (${step.id})
- Status: ${step.status}
- Duration: ${step.duration || 0}ms
- AST Debug Info Available: ${!!astDebug}
${astDebug?.llmSelections ? `- LLM Selections Count: ${astDebug.llmSelections.length}` : ''}
${astDebug?.validatedSelections ? `- Validated Selections Count: ${astDebug.validatedSelections.length}` : ''}
${astDebug?.spanMismatches ? `- Span Mismatches: ${astDebug.spanMismatches.length}` : ''}
${step.error ? `- Error: ${step.error}` : ''}
` : `- No AST debug info captured for ${step.name}`;
}).join('\n') : '- No AST validation steps found in this session'}

## Key Data Flows:
${debugSteps.map(step => step.input?.llmSelectionsFull ? `- LLM Selections: ${step.input.llmSelectionsFull.length} items` : '').filter(Boolean).join('\n')}
${debugSteps.map(step => step.output?.zenLenses ? `- Generated Lenses: ${step.output.zenLenses.length} items` : '').filter(Boolean).join('\n')}
\`\`\`
</file_contents>

${aiObservabilityData && aiObservabilityData.size > 0 ? `
## AI Prompts and Responses

${Array.from(aiObservabilityData.entries()).map(([stepId, data]) => `
### ${stepId.startsWith('semantic') ? '🧠 LLM Semantic Picks' : '🌸 Generate Lenses'} Prompt (${new Date(data.apiCall.timestamp).toLocaleTimeString()})

\`\`\`
${data.prompt.content}
\`\`\`

### Response Summary
- Status: ${data.response.statusCode}
- Response Time: ${data.response.responseTimeMs}ms
- Token Usage: Input ${data.prompt.tokenCount || 'N/A'} / Output ${data.response.tokenCount || 'N/A'}
- Content Type: ${Array.isArray(data.response.content) ? `Array(${data.response.content.length})` : typeof data.response.content}

---
`).join('')}
` : ''}

## Analysis Request

Please analyze this debug session and provide insights on:

1. **Pipeline Performance**: 
   - Are there bottlenecks or optimization opportunities?
   - Is the LLM-first approach working effectively?

2. **Error Analysis**:
   - What patterns do you see in the errors?
   - How can we improve error handling and recovery?

3. **Data Flow Issues**:
   - Are there problems with data transformation between steps?
   - How can we improve the LLM → AST → Lens pipeline?

4. **AST Validation Specific Issues**:
   - Why are partial token matches being used instead of full LLM selections?
   - How can we improve span validation between LLM selections and AST nodes?
   - What's the best approach to handle mismatches between text-based selections and AST positions?

5. **User Experience**:
   - Based on the debug data, what UX improvements would you suggest?
   - How can we make the pipeline more transparent to developers?

6. **Code Quality**:
   - What architectural improvements would you recommend?
   - Are there better patterns for this type of multi-step AI pipeline?

Please provide specific, actionable recommendations with code examples where appropriate.`;
                };
                
                const promptStr = generateLLMPrompt();
                const promptBlob = new Blob([promptStr], { type: 'text/markdown' });
                const url = URL.createObjectURL(promptBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `syntaxlens-debug-prompt-${Date.now()}.md`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
              title="Export LLM Analysis Prompt"
              style={{
                padding: '8px',
                background: 'transparent',
                border: '1px solid var(--zen-border)',
                borderRadius: '4px',
                color: 'var(--zen-text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              🤖
            </button>
            
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit Fullscreen (Esc)" : "Fullscreen (F11)"}
              style={{
                padding: '8px',
                background: 'transparent',
                border: '1px solid var(--zen-border)',
                borderRadius: '4px',
                color: 'var(--zen-text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isFullscreen ? '⊡' : '⊞'}
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                background: 'transparent',
                border: 'none',
                color: 'var(--zen-text-muted)',
                fontSize: '20px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          background: 'var(--zen-canvas)'
        }}>
          {/* Graph/List View */}
          <div style={{ 
            flex: showInputOutput && selectedNode ? '1' : '1', 
            display: 'flex',
            flexDirection: 'column',
            borderRight: showInputOutput && selectedNode ? '1px solid var(--zen-border)' : 'none'
          }}>
            {viewMode === 'graph' ? (
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionMode={ConnectionMode.Loose}
                fitView
                attributionPosition="bottom-left"
              >
                <Background color="#2d3748" gap={16} />
                <Controls 
                  style={{
                    background: 'var(--zen-card)',
                    border: '1px solid var(--zen-border)',
                    borderRadius: '8px'
                  }}
                />
                <MiniMap 
                  nodeColor={(node) => {
                    const data = node.data as DebugNodeData;
                    const colors = {
                      pending: '#718096',
                      running: '#4299e1',
                      completed: '#48bb78',
                      error: '#ef4444'
                    };
                    return colors[data.status] || '#718096';
                  }}
                  style={{
                    background: 'var(--zen-card)',
                    border: '1px solid var(--zen-border)',
                    borderRadius: '8px'
                  }}
                />
              </ReactFlow>
            ) : viewMode === 'list' ? (
              <div style={{ 
                flex: 1, 
                overflow: 'auto',
                padding: '24px'
              }}>
                {debugSteps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => {
                      setSelectedNode(step.id);
                      if (step.id === 'generate') {
                        setShowOutput(true);
                      }
                    }}
                    style={{
                      background: selectedNode === step.id ? 'var(--zen-card)' : 'var(--zen-pane)',
                      border: `1px solid ${selectedNode === step.id ? 'var(--zen-accent)' : 'var(--zen-border)'}`,
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ 
                          width: '32px', 
                          height: '32px',
                          borderRadius: '50%',
                          background: step.status === 'completed' ? '#48bb78' : 
                                     step.status === 'error' ? '#ef4444' :
                                     step.status === 'running' ? '#4299e1' : '#718096',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600
                        }}>
                          {index + 1}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--zen-text)' }}>{step.name}</div>
                          {step.duration && (
                            <div style={{ fontSize: '12px', color: 'var(--zen-text-muted)' }}>
                              {step.duration}ms
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: '20px' }}>
                        {step.status === 'completed' && '✓'}
                        {step.status === 'running' && '⏳'}
                        {step.status === 'error' && '✕'}
                        {step.status === 'pending' && '○'}
                      </div>
                    </div>
                    {step.error && (
                      <div style={{
                        marginTop: '12px',
                        padding: '8px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#ef4444'
                      }}>
                        {step.error}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              // AI Observability View
              <div style={{ 
                flex: 1, 
                overflow: 'auto',
                padding: '24px'
              }}>
                <h3 style={{ 
                  color: 'var(--zen-text)', 
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🤖 AI Observability Dashboard
                </h3>
                
                {aiObservabilityData && aiObservabilityData.size > 0 ? (
                  (() => {
                    console.log('🔍 AI Observability Panel - Rendering data:', {
                      dataSize: aiObservabilityData.size,
                      keys: Array.from(aiObservabilityData.keys()),
                      entries: Array.from(aiObservabilityData.entries()).map(([k, v]) => ({
                        key: k,
                        hasPrompt: !!v?.prompt?.content,
                        model: v?.metadata?.model
                      }))
                    });
                    return Array.from(aiObservabilityData.entries()).map(([stepId, data]) => (
                    <motion.div
                      key={stepId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        background: 'var(--zen-card)',
                        border: '1px solid var(--zen-border)',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '16px'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <h4 style={{ 
                          color: 'var(--zen-text)', 
                          margin: 0,
                          textTransform: 'capitalize'
                        }}>
                          {stepId.startsWith('semantic') ? '🧠 LLM Semantic Picks' : '🌸 Generate Lenses'} API Call
                          <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--zen-text-muted)', marginLeft: '8px' }}>
                            {new Date(data.apiCall.timestamp).toLocaleTimeString()}
                          </span>
                        </h4>
                        <span style={{
                          padding: '4px 8px',
                          background: data.response.statusCode === 200 ? '#48bb78' : '#ef4444',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {data.response.statusCode}
                        </span>
                      </div>
                      
                      {/* API Call Details */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--zen-text-muted)', marginBottom: '4px' }}>Endpoint</div>
                          <div style={{ color: 'var(--zen-text)', fontFamily: 'monospace' }}>{data.apiCall.endpoint}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--zen-text-muted)', marginBottom: '4px' }}>Response Time</div>
                          <div style={{ color: 'var(--zen-text)' }}>{data.response.responseTimeMs}ms</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--zen-text-muted)', marginBottom: '4px' }}>Model</div>
                          <div style={{ color: 'var(--zen-text)' }}>{data.metadata.model || 'N/A'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--zen-text-muted)', marginBottom: '4px' }}>Est. Cost</div>
                          <div style={{ color: 'var(--zen-text)' }}>
                            {data.metadata.cost ? `$${data.metadata.cost.toFixed(6)}` : 'N/A'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Token Usage */}
                      {(data.prompt.tokenCount || data.response.tokenCount) && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--zen-text)', marginBottom: '8px' }}>
                            📊 Token Usage
                          </div>
                          <div style={{ display: 'flex', gap: '16px' }}>
                            {data.prompt.tokenCount && (
                              <span style={{ color: 'var(--zen-text-muted)' }}>
                                Input: <strong style={{ color: 'var(--zen-text)' }}>{data.prompt.tokenCount}</strong>
                              </span>
                            )}
                            {data.response.tokenCount && (
                              <span style={{ color: 'var(--zen-text-muted)' }}>
                                Output: <strong style={{ color: 'var(--zen-text)' }}>{data.response.tokenCount}</strong>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Prompt Section */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 600, 
                          color: 'var(--zen-text)', 
                          marginBottom: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          📝 Prompt
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(data.prompt.content);
                            }}
                            style={{
                              padding: '2px 6px',
                              fontSize: '10px',
                              background: 'var(--zen-accent)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                          >
                            Copy
                          </button>
                        </div>
                        <div style={{
                          background: 'var(--zen-pane)',
                          padding: '12px',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color: 'var(--zen-text)',
                          maxHeight: '200px',
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {data.prompt.content.length > 500 
                            ? `${data.prompt.content.substring(0, 500)}...` 
                            : data.prompt.content}
                        </div>
                      </div>
                      
                      {/* Response Section */}
                      <div>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 600, 
                          color: 'var(--zen-text)', 
                          marginBottom: '8px'
                        }}>
                          📤 Response
                        </div>
                        <EnhancedDataViewer 
                          data={data.response.content} 
                          mode="json-tree"
                          searchQuery=""
                          style={{ maxHeight: '300px', background: 'var(--zen-pane)' }}
                        />
                      </div>
                    </motion.div>
                  ));
                  })()
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'var(--zen-text-muted)',
                    background: 'var(--zen-pane)',
                    borderRadius: '8px',
                    border: '1px dashed var(--zen-border)'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
                    <div style={{ fontSize: '18px', marginBottom: '8px' }}>No AI Observability Data</div>
                    <div style={{ fontSize: '14px' }}>
                      AI observability data will appear here when LLM API calls are made
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input/Output Inspector */}
          {showInputOutput && selectedNode && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: isFullscreen ? '40%' : '50%' }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--zen-pane)',
                overflow: 'hidden'
              }}
            >
              {selectedNodeData ? (
                <>
                  <div style={{
                    padding: '16px',
                    background: 'var(--zen-card)',
                    borderBottom: '1px solid var(--zen-border)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--zen-text)' }}>
                        {selectedNodeData.label}
                      </h3>
                  <button
                    onClick={() => setSelectedNode(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--zen-text-muted)',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ 
                  marginTop: '8px', 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '12px',
                  color: 'var(--zen-text-muted)'
                }}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span>Status: {selectedNodeData.status}</span>
                    {selectedNodeData.duration && <span>Duration: {selectedNodeData.duration}ms</span>}
                    {selectedNodeData.metadata?.tokens && <span>Tokens: {selectedNodeData.metadata.tokens}</span>}
                  </div>
                  
                  {/* Data View Controls */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* Data View Mode Selector */}
                    <select
                      value={dataViewMode}
                      onChange={(e) => setDataViewMode(e.target.value as any)}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--zen-card)',
                        color: 'var(--zen-text)',
                        border: '1px solid var(--zen-border)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      <option value="json-tree">🌳 Tree View</option>
                      <option value="monaco">📝 Code View</option>
                      <option value="table">📊 Table View</option>
                      <option value="grid">📋 Grid View</option>
                    </select>
                    
                    {/* Search Input */}
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--zen-card)',
                        color: 'var(--zen-text)',
                        border: '1px solid var(--zen-border)',
                        borderRadius: '4px',
                        fontSize: '11px',
                        width: '100px'
                      }}
                    />
                    
                    {/* Show Output Toggle Button */}
                    {(selectedNode === 'generate' || isFinalStep(selectedNode, debugSteps)) && (
                      <button
                        onClick={() => setShowOutput(!showOutput)}
                        style={{
                          padding: '4px 8px',
                          background: showOutput ? 'var(--zen-accent)' : 'transparent',
                          color: showOutput ? 'white' : 'var(--zen-text)',
                          border: '1px solid var(--zen-border)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 500
                        }}
                      >
                        {showOutput ? '📤 Hide Output' : '📤 Show Output'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                {/* Enhanced Input Section */}
                {selectedNodeData.input && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderBottom: '1px solid var(--zen-border)' }}>
                    <div style={{
                      padding: '12px 16px',
                      background: 'var(--zen-card)',
                      borderBottom: '1px solid var(--zen-border)',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'var(--zen-accent)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>📥 Input Data</span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: 'var(--zen-text-muted)' }}>
                          {Object.keys(selectedNodeData.input || {}).length} properties
                        </span>
                        <button
                          onClick={() => setInputFullscreen(true)}
                          title="Fullscreen View"
                          style={{
                            padding: '2px 6px',
                            background: 'transparent',
                            border: '1px solid var(--zen-border)',
                            borderRadius: '4px',
                            color: 'var(--zen-text)',
                            cursor: 'pointer',
                            fontSize: '10px'
                          }}
                        >
                          ⛶
                        </button>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <DataViewerFullscreenWrapper
                        isFullscreen={inputFullscreen}
                        onClose={() => setInputFullscreen(false)}
                        title="Input Data"
                        dataViewMode={dataViewMode}
                        setDataViewMode={setDataViewMode}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                      >
                        <EnhancedDataViewer
                          data={selectedNodeData.input}
                          mode={dataViewMode}
                          searchQuery={searchQuery}
                          title="Input"
                        />
                      </DataViewerFullscreenWrapper>
                    </div>
                  </div>
                )}

                {/* Enhanced Output Section - Show when toggle is enabled */}
                {showOutput ? (
                  <div 
                    key={`output-${selectedNode}-${showOutput}`} 
                    style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                  >
                    <div style={{
                      padding: '12px 16px',
                      background: 'var(--zen-card)',
                      borderBottom: '1px solid var(--zen-border)',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: 'var(--zen-success)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>📤 {selectedNode === 'resolve' ? 'AST Validation Data' : 'Output Data'}</span>
                      <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'var(--zen-text-muted)', alignItems: 'center' }}>
                        <span>
                          {(() => {
                            const outputData = selectedNodeData?.output || debugSteps.find(s => s.id === selectedNode)?.output;
                            if (!outputData) return '0 properties';
                            if (Array.isArray(outputData)) return `Array(${outputData.length})`;
                            if (typeof outputData === 'object') return `${Object.keys(outputData).length} properties`;
                            return typeof outputData;
                          })()}
                        </span>
                        <span>|</span>
                        <span style={{ color: 'var(--zen-success)' }}>
                          {selectedNodeData?.status === 'completed' ? '✅ Complete' : '⏳ Processing'}
                        </span>
                        <button
                          onClick={() => setOutputFullscreen(true)}
                          title="Fullscreen View"
                          style={{
                            padding: '2px 6px',
                            background: 'transparent',
                            border: '1px solid var(--zen-border)',
                            borderRadius: '4px',
                            color: 'var(--zen-text)',
                            cursor: 'pointer',
                            fontSize: '10px',
                            marginLeft: '8px'
                          }}
                        >
                          ⛶
                        </button>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <DataViewerFullscreenWrapper
                        isFullscreen={outputFullscreen}
                        onClose={() => setOutputFullscreen(false)}
                        title="Output Data"
                        dataViewMode={dataViewMode}
                        setDataViewMode={setDataViewMode}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                      >
                        <EnhancedDataViewer
                          data={selectedNodeData?.output || debugSteps.find(s => s.id === selectedNode)?.output || { message: "No output data available" }}
                          mode={dataViewMode}
                          searchQuery={searchQuery}
                          title="Output"
                        />
                      </DataViewerFullscreenWrapper>
                    </div>
                  </div>
                ) : null}

                {/* Error Section */}
                {selectedNodeData.error && (
                  <div style={{ 
                    padding: '16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderTop: '1px solid var(--zen-border)'
                  }}>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      color: '#ef4444',
                      marginBottom: '8px'
                    }}>
                      ❌ Error
                    </div>
                    <pre style={{ 
                      fontSize: '12px', 
                      color: '#ef4444',
                      whiteSpace: 'pre-wrap',
                      margin: 0
                    }}>
                      {typeof selectedNodeData.error === 'string' ? selectedNodeData.error : selectedNodeData.error.message}
                    </pre>
                  </div>
                )}
              </div>
                </>
              ) : (
                <div style={{ 
                  padding: '16px', 
                  color: 'var(--zen-text-muted)',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  Loading node data...
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* Enhanced Status Bar with Comprehensive Statistics */}
        <div style={{
          padding: '16px 24px',
          background: 'var(--zen-card)',
          borderTop: '1px solid var(--zen-border)',
          fontSize: '11px',
          color: 'var(--zen-text-muted)'
        }}>
          {/* Pipeline Status */}
          {currentState && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid var(--zen-border)'
            }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                <span>🔄 Phase: <strong style={{ color: 'var(--zen-accent)' }}>{currentState.phase}</strong></span>
                <span>⚓ Anchors: <strong>{currentState.anchorsCount}</strong></span>
                <span>🎯 Selected: <strong>{currentState.selectedAnchorsCount}</strong></span>
                <span>🔍 Lenses: <strong>{currentState.zenLensesCount}</strong></span>
                {currentState.isGenerating && <span style={{ color: 'var(--zen-accent)' }}>⏳ Generating...</span>}
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                {optimizationStats && (
                  <>
                    <span>💰 Tokens Saved: <strong>{optimizationStats.tokensSaved}</strong></span>
                    <span>⚡ Response: <strong>{Math.round(optimizationStats.responseTime)}ms</strong></span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Debug Steps Summary */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '20px' }}>
              <span>
                📊 Pipeline: <strong>{debugSteps.length}</strong> steps
              </span>
              <span>
                ✅ Completed: <strong style={{ color: '#48bb78' }}>
                  {debugSteps.filter(s => s.status === 'completed').length}
                </strong>
              </span>
              <span>
                ⏳ Running: <strong style={{ color: '#4299e1' }}>
                  {debugSteps.filter(s => s.status === 'running').length}
                </strong>
              </span>
              <span>
                ❌ Errors: <strong style={{ color: '#ef4444' }}>
                  {debugSteps.filter(s => s.status === 'error').length}
                </strong>
              </span>
              <span>
                ⏱️ Total Duration: <strong>
                  {debugSteps.reduce((sum, s) => sum + (s.duration || 0), 0)}ms
                </strong>
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {selectedNode && (
                <span>
                  🔍 Inspecting: <strong style={{ color: 'var(--zen-accent)' }}>
                    {debugSteps.find(s => s.id === selectedNode)?.name || selectedNode}
                  </strong>
                </span>
              )}
              <span style={{ 
                color: 'var(--zen-text-muted)', 
                fontSize: '10px',
                opacity: 0.7
              }}>
                View: {dataViewMode} | Search: {searchQuery || 'none'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};